import React, { useImperativeHandle, useRef, useState } from 'react'
import DateTimePicker, {
  ReactNativeModalDateTimePickerProps,
} from 'react-native-modal-datetime-picker'
import { useTheme } from '@ui-kitten/components'
import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Input, InputProps, InputRef } from '@components/base'
import { useIsDarkMode } from '@config/theme'

type PickerProps = Omit<
  ReactNativeModalDateTimePickerProps,
  'isDarkModeEnabled' | 'isVisible' | 'onConfirm' | 'onCancel'
>

export interface SelectDateTimeRef {
  focus: () => void
}

interface SelectDateTimeProps {
  inputProps?: InputProps
  pickerProps?: PickerProps
  style?: StyleProp<ViewStyle>
  ref?: React.Ref<SelectDateTimeRef>
  onChange: (time: Date) => void
}

export const SelectDateTime = ({
  inputProps = {},
  pickerProps = {},
  style,
  ref,
  onChange,
}: SelectDateTimeProps) => {
  const theme = useTheme()
  const styles = useStyleSheet(themedStyle)
  const isDarkMode = useIsDarkMode()

  const [isPickerVisible, setPickerVisible] = useState(false)

  const inputRef = useRef<InputRef>(null)

  const handlePress = () => {
    setPickerVisible(true)
    inputRef.current?.focus()
  }

  const handleConfirm = (time: Date) => {
    setPickerVisible(false)
    onChange(time)
  }

  // allow callers to focus the input
  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus()
    },
  }))

  return (
    <TouchableOpacity
      accessibilityLabel="Select date/time"
      disabled={inputProps.disabled}
      onPress={handlePress}
      style={style}
    >
      <View pointerEvents="none">
        <Input
          {...inputProps}
          caretHidden
          iconRight="caret-down"
          placeholder={inputProps.placeholder || 'Select'}
          ref={inputRef}
          showSoftInputOnFocus={false}
        />
        <DateTimePicker
          {...pickerProps}
          isDarkModeEnabled={isDarkMode}
          isVisible={isPickerVisible}
          focusable={false}
          onCancel={() => setPickerVisible(false)}
          onConfirm={handleConfirm}
          pickerContainerStyleIOS={styles.pickerContainerIOS}
          textColor={theme['theme.text.primary']}
          themeVariant={isDarkMode ? 'dark' : 'light'}
        />
      </View>
    </TouchableOpacity>
  )
}

const themedStyle = StyleService.create({
  pickerContainerIOS: {
    backgroundColor: 'theme.secondary.base',
  },
})
